import type { PublicSlide } from "@engage/contracts";
import { matchesAcceptedAnswer } from "@engage/scoring/typed";
import { useI18n } from "./i18n";
import type { Round } from "./live";

/**
 * What the room chose, once the presenter reveals it — and, on a scored question,
 * whether this participant got it right and what it earned them.
 */
export function Results({
  slide,
  round,
  lastAwarded,
}: {
  slide: PublicSlide;
  round: Round;
  lastAwarded: number | null;
}) {
  const { t } = useI18n();
  const tallies = round.results?.tallies ?? {};
  const correct = round.results?.correctOptionIds;
  const accepted = round.results?.acceptedAnswers;
  const mine = round.mine && "optionIds" in round.mine ? round.mine.optionIds : [];
  const typed = round.mine && "text" in round.mine ? round.mine.text : null;
  // Marked here the way the server marked it — the same function — so the verdict
  // survives a reconnect that lost the answer's own reply.
  const gotItRight =
    accepted !== undefined
      ? typed !== null && matchesAcceptedAnswer(typed, accepted)
      : correct !== undefined && mine.length === correct.length && mine.every((id) => correct.includes(id));
  const marked = correct !== undefined || accepted !== undefined;

  const hasBreakdown =
    slide.type === "mcq" || slide.type === "true_false" || slide.type === "poll" || slide.type === "scale" || slide.type === "word_cloud";

  return (
    <div class="stack">
      {slide.isScored && marked && round.answered && (
        <div class={`verdict ${gotItRight ? "right" : "wrong"}`}>
          <span class="verdict-icon" aria-hidden="true">
            {gotItRight ? "✓" : "✕"}
          </span>
          <strong>{gotItRight ? t("live.correct") : t("live.incorrect")}</strong>
          {gotItRight && lastAwarded !== null && lastAwarded > 0 && <span class="award">{t("live.points", { points: lastAwarded })}</span>}
        </div>
      )}
      {/* Results follow the close at once, so this is where a phone that never
          answered learns it — there is no "time's up" screen in between any more. */}
      {slide.isScored && !round.answered && (
        <div class="verdict missed">
          <strong>{t("live.missed")}</strong>
        </div>
      )}

      {accepted && <p class="card center">{t("live.answerWas", { answer: accepted.join(" · ") })}</p>}

      {hasBreakdown && (
        <div class="card stack">
          {(slide.type === "mcq" || slide.type === "true_false" || slide.type === "poll") && (
            <Bars
              rows={slide.options.map((o) => ({
                key: o.id,
                label: o.label,
                count: tallies[o.id] ?? 0,
                highlight: correct?.includes(o.id) ?? false,
                mine: mine.includes(o.id),
              }))}
            />
          )}

          {slide.type === "scale" && <ScaleResult slide={slide} tallies={tallies} />}

          {slide.type === "word_cloud" && (
            <ul class="words">
              {Object.entries(tallies)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 20)
                .map(([word, count]) => (
                  <li key={word}>
                    {word} <span class="muted">{count}</span>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Bars({ rows }: { rows: Array<{ key: string; label: string; count: number; highlight: boolean; mine: boolean }> }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ul class="bars">
      {rows.map((r) => (
        <li key={r.key} class={r.highlight ? "correct" : ""}>
          <div class="bar-label">
            <span>
              {r.label}
              {r.highlight && " ✓"}
              {r.mine && " •"}
            </span>
            <span class="muted">{r.count}</span>
          </div>
          <div class="track">
            <div class="fill" style={{ width: `${(r.count / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function ScaleResult({ slide, tallies }: { slide: PublicSlide; tallies: Record<string, number> }) {
  const { t } = useI18n();
  let n = 0;
  let sum = 0;
  for (let v = slide.scaleMin; v <= slide.scaleMax; v++) {
    const c = tallies[String(v)] ?? 0;
    n += c;
    sum += v * c;
  }
  return n > 0 ? <p class="big center">{t("live.average", { value: (sum / n).toFixed(1) })}</p> : null;
}
